import { useRouter } from 'next/router'

import { Product } from '@mc/api'
import { useCollapsedPaintedDoorExperiment } from '@mc/checkout'
import { useStaticData } from '@mc/static-data-react'
import { paths } from '@mc/paths'
import { useBogo } from '@mc/bogo-utils'
import { useGuestPass } from '@mc/free-trial'
import { getGiftPageFaqSection } from '@mc/promos'

import { getGiftingFaqSection } from 'experiments/pen-5044_v1_gift_plans_page'

import { getGeneralSection } from '../data/getGeneralSection'
import { getPricingSection } from '../data/getPricingSection'
import { getSessionsSection } from '../data/getSessionsSection'
import { getBogoSection } from '../data/getBogoSection'
import { getGuestPassSection } from '../data/getGuestPassSection'

export type Question = {
  title: string
  uuid: string
  answer: string | React.ReactNode
  hide?: boolean
}

export type Section = {
  title: string
  questions: Question[]
}

export type SectionsOptions = Array<
  | 'general'
  | 'pricing'
  | 'sessions'
  | 'bogo'
  | 'promo'
  | 'guest-pass'
  | 'gift-pen-5044_v1' // pen-5044_v1 experiment
  | 'gift-pen-5108_v1' // pen-5108_v1 experiment
>

export const useFaqData = ({
  personaFAQ = false,
  sections = ['general', 'pricing', 'sessions'],
}: {
  personaFAQ?: boolean
  sections?: SectionsOptions
}) => {
  const { pathname } = useRouter()
  const isCheckoutPage = pathname.startsWith(paths.checkout)

  const { annualPass } = useStaticData<{ annualPass: Product }>()
  const { isOn: isEntryPaintedDoorOn, isVariant: isEntryPaintedDoorVariant } =
    useCollapsedPaintedDoorExperiment()
  const { isBogoOfferEligible } = useBogo()
  const guestPass = useGuestPass()

  const generalSection = getGeneralSection({
    isEntryPaintedDoorOn,
    personaFAQ,
  })
  const pricingSection = getPricingSection({
    isCheckoutPage,
    annualPass,
    isEntryPaintedDoorVariant,
  })
  const sessionsSection = getSessionsSection({ isEntryPaintedDoorVariant })
  const bogoSection = getBogoSection()
  const guestPassSection = getGuestPassSection({ guestPass })
  const giftSection = getGiftingFaqSection() // pen-5044_v1 experiment
  const giftPageSection = getGiftPageFaqSection() // pen-5108_v1 experiment

  const faqData: Section[] = []
  if (sections?.length) {
    sections.forEach((section) => {
      if (section === 'general') faqData.push(generalSection)
      if (section === 'pricing') faqData.push(pricingSection)
      if (section === 'sessions') faqData.push(sessionsSection)
      if (section === 'bogo' && isBogoOfferEligible) faqData.push(bogoSection)
      if (section === 'guest-pass' && guestPass?.eligible)
        faqData.push(guestPassSection)
      if (section === 'gift-pen-5044_v1') faqData.push(giftSection)
      if (section === 'gift-pen-5108_v1') faqData.push(giftPageSection)
    })
  }

  return { faqData }
}
